class ProductPage {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('.product-form');
    this.optionInputs = [...section.querySelectorAll('[data-product-option]')];
    this.variantInput = section.querySelector('[data-product-variant-id]');
    this.submitButton = section.querySelector('[data-add-to-cart]');
    this.status = section.querySelector('[data-product-status]');
    this.mediaList = section.querySelector('[data-product-media-list]');
    this.mediaItems = [...section.querySelectorAll('[data-product-media-id]')];
    this.variants = this.readVariants();
    this.currentVariant = this.findVariant();
    this.busy = false;

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.onVariantChange());
    });
    section.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      thumbnail.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollToMedia(thumbnail.dataset.mediaThumbnail);
        this.updateMediaThumbnail(thumbnail.dataset.mediaThumbnail);
      });
    });
    section.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.moveGallery(-1));
    section.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.moveGallery(1));
    this.mediaList?.addEventListener('scroll', () => this.onGalleryScroll(), { passive: true });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.updateMediaThumbnail();
    this.updateSelectedOptions();
  }

  readVariants() {
    const script = this.section.querySelector('[data-product-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  findVariant() {
    if (!this.optionInputs.length) {
      return this.variants.find(
        (variant) => String(variant.id) === this.variantInput?.value
      ) || this.variants[0];
    }

    const selectedOptions = this.optionInputs
      .filter((input) => input.checked)
      .map((input) => input.value);
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );
  }

  formatMoney(cents) {
    const currency = this.section.dataset.currency || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang, {
      style: 'currency',
      currency,
    }).format(cents / 100);
  }

  onVariantChange() {
    this.currentVariant = this.findVariant();
    this.updateSelectedOptions();
    this.updateVariantState();
    this.updateMedia();
    this.updateUrl();
  }

  updateVariantState() {
    const variant = this.currentVariant;
    const currentPrice = this.section.querySelector('[data-current-price]');
    const priceLabel = this.section.querySelector('[data-price-label]');
    const comparePrice = this.section.querySelector('[data-compare-price]');
    const saving = this.section.querySelector('[data-price-saving]');
    const availability = this.section.querySelector('[data-product-availability]');
    const priceWrapper = this.section.querySelector('[data-product-price]');
    const sku = this.section.querySelector('[data-product-sku]');
    const skuWrapper = this.section.querySelector('[data-sku-wrapper]');
    const dynamicCheckout = this.section.querySelector('[data-dynamic-checkout]');

    if (this.variantInput) {
      this.variantInput.value = variant?.id || '';
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (currentPrice && variant) {
      currentPrice.textContent = this.formatMoney(variant.price);
      currentPrice.classList.toggle(
        'price--sale',
        variant.compare_at_price > variant.price
      );
    }

    if (priceLabel && variant) {
      priceLabel.textContent = variant.compare_at_price > variant.price
        ? this.section.dataset.salePriceText
        : this.section.dataset.regularPriceText;
    }

    if (priceWrapper) priceWrapper.hidden = !variant;

    if (comparePrice) {
      const onSale = Boolean(
        variant && variant.compare_at_price > variant.price
      );
      comparePrice.hidden = !onSale;
      if (onSale) comparePrice.textContent = this.formatMoney(variant.compare_at_price);
    }

    if (saving) {
      const onSale = Boolean(variant && variant.compare_at_price > variant.price);
      saving.hidden = !onSale;
      if (onSale) {
        saving.textContent = `-${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%`;
      }
    }

    if (availability) {
      availability.textContent = variant?.available
        ? this.section.dataset.inStockText
        : this.section.dataset.unavailableText;
      availability.classList.toggle('is-unavailable', !variant?.available);
    }

    if (sku && skuWrapper) {
      sku.textContent = variant?.sku || '';
      skuWrapper.hidden = !variant?.sku;
    }

    if (this.submitButton) {
      this.submitButton.disabled = !variant?.available;
      this.submitButton.textContent = !variant
        ? this.submitButton.dataset.unavailableText
        : variant.available
          ? this.submitButton.dataset.addText
          : this.submitButton.dataset.soldOutText;
    }

    if (dynamicCheckout) dynamicCheckout.hidden = !variant?.available;
  }

  updateMedia() {
    const mediaId = this.currentVariant?.featured_media?.id;
    if (!mediaId) return;

    this.scrollToMedia(mediaId);
    this.updateMediaThumbnail(mediaId);
  }

  updateSelectedOptions() {
    this.section.querySelectorAll('[data-product-option-group]').forEach((group) => {
      const selected = group.querySelector('[data-product-option]:checked');
      const label = group.querySelector('[data-selected-option]');
      if (label) label.textContent = selected?.value || '';
    });
  }

  scrollToMedia(mediaId) {
    const media = this.section.querySelector(`[data-product-media-id="${mediaId}"]`);
    if (!media || !this.mediaList) return;

    this.mediaList.scrollTo({
      left: media.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  moveGallery(direction) {
    if (!this.mediaItems.length || !this.mediaList) return;

    const currentIndex = Math.round(this.mediaList.scrollLeft / this.mediaList.clientWidth);
    const nextIndex = (currentIndex + direction + this.mediaItems.length) % this.mediaItems.length;
    this.scrollToMedia(this.mediaItems[nextIndex].dataset.productMediaId);
  }

  onGalleryScroll() {
    if (this.galleryFrame) return;

    this.galleryFrame = window.requestAnimationFrame(() => {
      const currentIndex = Math.min(
        this.mediaItems.length - 1,
        Math.max(0, Math.round(this.mediaList.scrollLeft / this.mediaList.clientWidth))
      );
      const currentMedia = this.mediaItems[currentIndex];
      if (currentMedia) this.updateMediaThumbnail(currentMedia.dataset.productMediaId);
      this.galleryFrame = null;
    });
  }

  updateMediaThumbnail(mediaId = this.currentVariant?.featured_media?.id) {
    this.section.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      if (String(thumbnail.dataset.mediaThumbnail) === String(mediaId)) {
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });
  }

  updateUrl() {
    if (!this.currentVariant || !window.history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  async parseResponse(response) {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.busy || !this.currentVariant?.available || !this.submitButton) return;

    this.busy = true;
    this.form.setAttribute('aria-busy', 'true');
    this.submitButton.disabled = true;
    this.submitButton.textContent = this.submitButton.dataset.loadingText;
    this.status?.classList.remove('is-error');
    if (this.status) this.status.textContent = '';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const formData = new FormData(this.form);
      formData.set('sections', 'cart-drawer');
      formData.set('sections_url', window.location.pathname);
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await this.parseResponse(response);

      if (!response.ok) {
        const responseMessage = data.description || data.message;
        throw new Error(
          typeof responseMessage === 'string'
            ? responseMessage
            : this.submitButton.dataset.errorText
        );
      }

      this.submitButton.textContent = this.submitButton.dataset.successText;
      try {
        const drawerHtml = data.sections?.['cart-drawer'];
        if (drawerHtml) {
          window.catalogCartDrawer?.render(drawerHtml, { open: true });
        }
        await window.catalogCartDrawer?.refreshCart();
      } catch {
        if (this.status) {
          this.status.textContent = this.submitButton.dataset.refreshErrorText;
        }
      }
    } catch (error) {
      this.submitButton.textContent = this.submitButton.dataset.addText;
      if (this.status) {
        this.status.textContent = error.message || this.submitButton.dataset.errorText;
        this.status.classList.add('is-error');
      }
    } finally {
      this.busy = false;
      this.form.removeAttribute('aria-busy');
      this.submitButton.disabled = !this.currentVariant?.available;

      if (this.submitButton.textContent === this.submitButton.dataset.successText) {
        window.setTimeout(() => {
          this.submitButton.textContent = this.submitButton.dataset.addText;
        }, 1500);
      }
    }
  }
}

const initializeProductPages = (scope = document) => {
  scope
    .querySelectorAll('[data-product-section]:not([data-product-initialized])')
    .forEach((section) => {
      section.dataset.productInitialized = 'true';
      new ProductPage(section);
    });
};

initializeProductPages();

document.addEventListener('shopify:section:load', (event) => {
  initializeProductPages(event.target);
});
